import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MilestoneWithJob } from '@/lib/types/milestone';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  getMilestoneStatusLabel, 
  getMilestoneStatusIcon,
  groupMilestonesByJob,
  isMilestoneUserResponsibility 
} from '@/lib/milestoneCalendarUtils';
import { useAuth } from '@/lib/auth';
import { ExternalLink } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

interface MilestoneDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  milestones: MilestoneWithJob[];
  selectedDate: Date;
}

export const MilestoneDetailsDialog = ({
  open,
  onOpenChange,
  milestones,
  selectedDate,
}: MilestoneDetailsDialogProps) => {
  const { user } = useAuth();
  const groupedByJob = groupMilestonesByJob(milestones);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>
            Milestones for {format(selectedDate, 'MMMM d, yyyy')}
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6">
            {Array.from(groupedByJob.entries()).map(([jobId, jobMilestones]) => {
              const firstMilestone = jobMilestones[0];
              
              return (
                <div key={jobId} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-lg">{firstMilestone.job_title}</h3>
                      <p className="text-sm text-muted-foreground">
                        Job ID: {firstMilestone.job_public_id}
                      </p>
                    </div>
                    <Link to={`/jobs/${firstMilestone.job_public_id}/milestones`}>
                      <Button variant="outline" size="sm">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Job
                      </Button>
                    </Link>
                  </div>

                  <div className="space-y-3">
                    {jobMilestones.map((milestone) => {
                      const StatusIcon = getMilestoneStatusIcon(milestone);
                      const isUserResp = user?.role && isMilestoneUserResponsibility(milestone, user.role);
                      
                      return (
                        <div
                          key={milestone.id}
                          className={`p-4 rounded-lg border ${
                            isUserResp 
                              ? 'border-primary bg-primary/5' 
                              : 'border-border bg-muted/30'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium">{milestone.milestone_name}</h4>
                                {StatusIcon && (
                                  <StatusIcon className={`h-4 w-4 ${
                                    milestone.is_completed 
                                      ? 'text-green-500' 
                                      : 'text-destructive'
                                  }`} />
                                )}
                              </div>
                              
                              <div className="flex flex-wrap gap-2">
                                <Badge variant="outline">
                                  {milestone.milestone_type}
                                </Badge>
                                <Badge variant={isUserResp ? 'default' : 'secondary'}>
                                  {milestone.action_owner === 'client' ? 'Client' : 'Vendor'} Action
                                </Badge>
                                <Badge variant={
                                  milestone.is_completed 
                                    ? 'default' 
                                    : new Date(milestone.due_date) < new Date() 
                                    ? 'destructive' 
                                    : 'outline'
                                }>
                                  {getMilestoneStatusLabel(milestone)}
                                </Badge>
                              </div>

                              <div className="text-sm space-y-1">
                                <p>
                                  <span className="font-medium">Deliverable:</span>{' '}
                                  {milestone.deliverable}
                                </p>
                                {milestone.description && (
                                  <p>
                                    <span className="font-medium">Description:</span>{' '}
                                    {milestone.description}
                                  </p>
                                )}
                                {milestone.is_completed && milestone.completed_at && (
                                  <p className="text-muted-foreground">
                                    Completed on {format(new Date(milestone.completed_at), 'MMM d, yyyy')}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <Separator />
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
